import React, { createContext, useContext, useEffect, useState } from 'react';
import pb, { getCurrentUser, isAuthenticated, logout as pbLogout } from '../services/pocketbase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const restoreSession = async () => {
      if (!isAuthenticated()) {
        if (active) {
          setUser(null);
          setLoading(false);
        }
        return;
      }

      try {
        // Validate a persisted token rather than trusting stale browser state.
        const authData = await pb.collection('users').authRefresh();
        if (active) setUser(authData.record);
      } catch (error) {
        console.warn('Stored Video Hub session is no longer valid:', error);
        pb.authStore.clear();
        if (active) setUser(null);
      } finally {
        if (active) setLoading(false);
      }
    };

    restoreSession();

    const unsubscribe = pb.authStore.onChange(() => {
      if (!active) return;
      setUser(pb.authStore.isValid ? getCurrentUser() : null);
    });

    return () => {
      active = false;
      unsubscribe?.();
    };
  }, []);

  const login = async (email, password) => {
    const authData = await pb.collection('users').authWithPassword(
      String(email || '').trim(),
      password
    );
    setUser(authData.record);
    return authData;
  };

  const logout = () => {
    pbLogout();
    setUser(null);
  };

  const value = {
    user,
    login,
    logout,
    isAuthenticated: Boolean(user && pb.authStore.isValid),
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};